import { useState, useEffect } from 'react'
import { LandingPageData } from '@/types/main'
import { mainScreenService } from '@/services/mainScreenService'
import { useAccessTier } from '@/hooks/useAccessTier'

/**
 * The dashboard's trending and discounted feeds.
 *
 * 🔴 Both feeds are financial and neither has a non-financial counterpart, so
 * a session that has not completed verification does not fetch them. `gated`
 * is how the screen tells "we asked and got nothing" apart from "we never
 * asked" — the difference between rendering an empty state and rendering the
 * lock.
 *
 * The `params` argument is gone: the only caller never passed one, and it was
 * the last thing keeping a fragile `stableParams`/`paramsRef` comparison alive
 * whose `data` dependency made the effect re-run on its own result.
 */
export const useMainScreen = () => {
  const { tier, canReadFinancials } = useAccessTier()
  const [data, setData] = useState<LandingPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (tier === 'unresolved') {
      // Hold. Fetching before the entitlement is known would either paint real
      // numbers we then have to take away, or waste a request we cannot use.
      setLoading(true)
      return
    }

    if (!canReadFinancials) {
      // 🔴 Clear, do not merely stop fetching. A user who somehow reached the
      // browse tier after holding the full tier in the same mounted tree must
      // not keep the rows already in state.
      setData(null)
      setError(null)
      setLoading(false)
      return
    }

    let isMounted = true
    setLoading(true)

    mainScreenService
      .getLandingPageData()
      .then(result => {
        if (!isMounted) return
        setData(result)
        setError(null)
      })
      .catch(err => {
        if (!isMounted) return
        setData(null)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [tier, canReadFinancials])

  return {
    data,
    loading,
    error,
    /** No feed will arrive for this session. Render the lock, not an empty list. */
    gated: tier !== 'unresolved' && !canReadFinancials,
  }
}
